/**
 * Event names shared by the current Home page and the Your Journey widget that replaces it.
 *
 * Both write to `user_actions` through `useUserEvents().trackEvent()`, so the two
 * implementations have to agree on the strings for the before/after comparison to mean
 * anything. `user-events.store.js` spreads these maps into its own `USER_EVENTS` and
 * `EVENT_DESCRIPTIONS` — a name missing from either is silently dropped.
 *
 * The backend reads `home_viewed` / `home_cta_clicked` in `HomeBaselineService`; the
 * strings are duplicated there as class constants.
 */

export const HOME_EVENTS = {
	HOME_VIEWED: 'home_viewed',
	HOME_CTA_CLICKED: 'home_cta_clicked',
};

/**
 * The twelve events from the Ideal User Journey PRD §14. Defined ahead of the Journey
 * implementation (EN-997 onwards) so the baseline and the replacement share one list.
 *
 * `interview_practice_started` and `gmail_connected` already fire from InterviewAI and
 * JobTrackerAI respectively — the Journey adds a `source` property rather than a new name.
 */
export const JOURNEY_EVENTS = {
	JOURNEY_WIDGET_VIEWED: 'journey_widget_viewed',
	JOURNEY_CTA_CLICKED: 'journey_cta_clicked',
	JOURNEY_HEADER_BUTTON_OPENED: 'journey_header_button_opened',
	JOURNEY_HEADER_ACTION_CLICKED: 'journey_header_action_clicked',
	CHANGE_FOCUS_OPENED: 'change_focus_opened',
	FOCUS_SWITCHED: 'focus_switched',
	FOCUS_SWITCHED_BACK: 'focus_switched_back',
	RECOMMENDATION_ISSUE_REPORTED: 'recommendation_issue_reported',
	PROFILE_FIELD_CONFIRMED_INLINE: 'profile_field_confirmed_inline',
	GMAIL_CONNECT_CLICKED: 'gmail_connect_clicked',
	GMAIL_CONNECTED: 'gmail_connected',
	INTERVIEW_PRACTICE_STARTED: 'interview_practice_started',
};

/**
 * UI events from the AutoApply Queue (EN-1252). Status changes themselves are recorded server-side as
 * `autoapply_queue_status_changed` rows; these are the actions and views that never reach the server as a
 * status change. Events about one application carry its job, uuid and queue status, the toggle and tutorial
 * events carry their own fields, and all of them carry `platform` (`mobile|desktop`). The helpers that send
 * them are in `composables/autoapply-queue-analytics.js`.
 */
export const AUTOAPPLY_QUEUE_EVENTS = {
	REVIEW_OPENED: 'autoapply_queue_review_opened',
	SUBMITTED: 'autoapply_queue_submitted',
	SKIPPED: 'autoapply_queue_skipped',
	SEND_CANCELLED: 'autoapply_queue_send_cancelled',
	REMOVED: 'autoapply_queue_removed',
	ISSUE_REPORTED: 'autoapply_queue_issue_reported',
	MARKED_APPLIED: 'autoapply_queue_marked_applied',
	COMPLETE_AUTO_APPLY_TOGGLED: 'autoapply_queue_complete_auto_apply_toggled',
	TUTORIAL_STEP_SHOWN: 'autoapply_queue_tutorial_step_shown',
	TUTORIAL_CLOSED: 'autoapply_queue_tutorial_closed',
};

/**
 * Keyed by event value, not by constant name, because `EVENT_DESCRIPTIONS` in
 * `user-events.store.js` is keyed that way and the two are merged.
 */
export const ANALYTICS_EVENT_DESCRIPTIONS = {
	[HOME_EVENTS.HOME_VIEWED]: 'User opened Home, with the stats that describe their cohort',
	[HOME_EVENTS.HOME_CTA_CLICKED]: 'User clicked a card, tool or resource on Home',
	[JOURNEY_EVENTS.JOURNEY_WIDGET_VIEWED]: 'User saw the Your Journey widget',
	[JOURNEY_EVENTS.JOURNEY_CTA_CLICKED]: 'User clicked an action in the Your Journey focus block',
	[JOURNEY_EVENTS.JOURNEY_HEADER_BUTTON_OPENED]: 'User opened the Your Journey header popover',
	[JOURNEY_EVENTS.JOURNEY_HEADER_ACTION_CLICKED]: 'User clicked an action in the Your Journey header popover',
	[JOURNEY_EVENTS.CHANGE_FOCUS_OPENED]: 'User opened the Change Focus dialog',
	[JOURNEY_EVENTS.FOCUS_SWITCHED]: 'User switched their focus to another stage',
	[JOURNEY_EVENTS.FOCUS_SWITCHED_BACK]: 'User switched back to the recommended focus',
	[JOURNEY_EVENTS.RECOMMENDATION_ISSUE_REPORTED]: 'User reported an issue with the recommendation',
	[JOURNEY_EVENTS.PROFILE_FIELD_CONFIRMED_INLINE]: 'User confirmed a pre-filled profile field from the widget',
	[JOURNEY_EVENTS.GMAIL_CONNECT_CLICKED]: 'User started connecting Gmail',
	[JOURNEY_EVENTS.GMAIL_CONNECTED]: 'User connects Gmail to JobTrackerAI',
	[JOURNEY_EVENTS.INTERVIEW_PRACTICE_STARTED]: 'User launched an interview with InterviewAI',
	[AUTOAPPLY_QUEUE_EVENTS.REVIEW_OPENED]: 'User opened an application in the AutoApply Queue review',
	[AUTOAPPLY_QUEUE_EVENTS.SUBMITTED]: 'User submitted an application from the AutoApply Queue review',
	[AUTOAPPLY_QUEUE_EVENTS.SKIPPED]: 'User skipped an application in the AutoApply Queue review',
	[AUTOAPPLY_QUEUE_EVENTS.SEND_CANCELLED]: 'User cancelled an AutoApply Queue application that was about to send',
	[AUTOAPPLY_QUEUE_EVENTS.REMOVED]: 'User removed an application from the AutoApply Queue',
	[AUTOAPPLY_QUEUE_EVENTS.ISSUE_REPORTED]: 'User reported an issue with an application in the AutoApply Queue',
	[AUTOAPPLY_QUEUE_EVENTS.MARKED_APPLIED]: 'User marked a queued application as applied outside AutoApply',
	[AUTOAPPLY_QUEUE_EVENTS.COMPLETE_AUTO_APPLY_TOGGLED]: 'User toggled complete auto apply in the AutoApply Queue',
	[AUTOAPPLY_QUEUE_EVENTS.TUTORIAL_STEP_SHOWN]: 'User saw a step of an AutoApply Queue tutorial',
	[AUTOAPPLY_QUEUE_EVENTS.TUTORIAL_CLOSED]: 'User closed an AutoApply Queue tutorial',
};
